import { useState, useEffect } from 'react';

interface Breakpoints {
  xs: number;
  sm: number;
  md: number;
  lg: number;
  xl: number;
  xxl: number;
}

// Default breakpoints (matching Ant Design's breakpoints)
const defaultBreakpoints: Breakpoints = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1600,
};

interface ResponsiveLayoutReturn {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  screenWidth: number;
  screenHeight: number;
  breakpoint: keyof Breakpoints;
}

export const useResponsiveLayout = (
  breakpoints: Partial<Breakpoints> = {},
  mobileBreakpoint: keyof Breakpoints = 'md'
): ResponsiveLayoutReturn => {
  const [screenDimensions, setScreenDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  });

  const mergedBreakpoints = { ...defaultBreakpoints, ...breakpoints };

  useEffect(() => {
    const updateDimensions = () => {
      setScreenDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    // Set initial dimensions
    updateDimensions();

    // Add event listener for window resize
    window.addEventListener('resize', updateDimensions);

    // Cleanup
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const { width } = screenDimensions;

  // Determine current breakpoint
  const getCurrentBreakpoint = (): keyof Breakpoints => {
    if (width >= mergedBreakpoints.xxl) return 'xxl';
    if (width >= mergedBreakpoints.xl) return 'xl';
    if (width >= mergedBreakpoints.lg) return 'lg';
    if (width >= mergedBreakpoints.md) return 'md';
    if (width >= mergedBreakpoints.sm) return 'sm';
    return 'xs';
  };

  const currentBreakpoint = getCurrentBreakpoint();
  const mobileBreakpointValue = mergedBreakpoints[mobileBreakpoint];

  return {
    isMobile: width < mobileBreakpointValue,
    isTablet: width >= mergedBreakpoints.sm && width < mergedBreakpoints.lg,
    isDesktop: width >= mergedBreakpoints.lg,
    screenWidth: width,
    screenHeight: screenDimensions.height,
    breakpoint: currentBreakpoint,
  };
};
